import React, { useState } from 'react';
import { View, Text, TextInput, Pressable, StyleProp, ViewStyle, Modal, FlatList, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { FormLabel } from './form';

interface SelectOption {
    label: string;
    value: string;
}

interface SelectInputProps {
    label?: string;
    value: string | null;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    style?: StyleProp<ViewStyle>;
}

const SelectInput: React.FC<SelectInputProps> = ({
    label,
    value,
    onChange,
    options,
    placeholder = 'Select an option',
    style
}) => {
    const [showModal, setShowModal] = useState(false);
    const [tempValue, setTempValue] = useState<string | null>(value);

    const getCurrentLabel = () => {
        if (!value) return '';
        const option = options.find(opt => opt.value === value);
        return option ? option.label : '';
    };

    const openSelector = () => {
        setTempValue(value);
        setShowModal(true);
    };

    const handleSelect = (selectedValue: string) => {
        setTempValue(selectedValue);
    };

    const handleConfirm = () => {
        if (tempValue !== null) {
            onChange(tempValue);
        }
        setShowModal(false);
    };

    const handleCancel = () => {
        setTempValue(value);
        setShowModal(false);
    };

    return (
        <View style={[{ padding: 0, margin: 0 }, style]}>
            {label && <FormLabel className="text-base">{label}</FormLabel>}
            <Pressable style={{ marginTop: 0 }} onPress={openSelector}>
                <View
                    style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        height: 44,
                        width: '100%',
                        borderWidth: 1,
                        borderColor: '#ddd',
                        borderRadius: 8,
                        paddingHorizontal: 12,
                        backgroundColor: '#fff',
                    }}
                >
                    <TextInput
                        style={{
                            flex: 1,
                            fontSize: 15,
                            color: value ? '#000' : '#9ca3af',
                        }}
                        placeholder={placeholder}
                        placeholderTextColor="#9ca3af"
                        value={getCurrentLabel()}
                        editable={false}
                    />
                    <Ionicons name="chevron-down" size={18} color="#666" />
                </View>
            </Pressable>

            <Modal
                animationType="fade"
                transparent={true}
                visible={showModal}
                onRequestClose={handleCancel}
            >
                <Pressable
                    style={{
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    }}
                    onPress={handleCancel}
                >
                    <View
                        style={{
                            backgroundColor: 'white',
                            borderRadius: 12,
                            padding: 20,
                            width: '90%',
                            maxWidth: 400,
                            maxHeight: '80%',
                            shadowColor: "#000",
                            shadowOffset: { width: 0, height: 2 },
                            shadowOpacity: 0.25,
                            shadowRadius: 4,
                            elevation: 5,
                        }}
                        onStartShouldSetResponder={() => true}
                        onTouchEnd={(e) => e.stopPropagation()}
                    >
                        <Text style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 15, textAlign: 'center' }}>
                            {label || 'Select an option'}
                        </Text>

                        <FlatList
                            data={options}
                            keyExtractor={(item) => item.value}
                            style={{ maxHeight: 300 }}
                            renderItem={({ item }) => (
                                <TouchableOpacity
                                    style={{
                                        paddingVertical: 12,
                                        paddingHorizontal: 16,
                                        borderRadius: 8,
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        backgroundColor: item.value === tempValue ? '#f0f0f0' : 'transparent',
                                    }}
                                    onPress={() => handleSelect(item.value)}
                                >
                                    <Text style={{
                                        flex: 1,
                                        fontSize: 16,
                                        fontWeight: item.value === tempValue ? '600' : 'normal'
                                    }}>
                                        {item.label}
                                    </Text>
                                    {item.value === tempValue && (
                                        <Ionicons name="checkmark" size={20} color="#000" />
                                    )}
                                </TouchableOpacity>
                            )}
                            ItemSeparatorComponent={() => <View style={{ height: 1, backgroundColor: '#eee' }} />}
                        />

                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15 }}>
                            <Button
                                variant="outline"
                                onPress={handleCancel}
                                style={{ flex: 1, marginRight: 8 }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="crew"
                                onPress={handleConfirm}
                                style={{ flex: 1, marginLeft: 8 }}
                            >
                                Confirm
                            </Button>
                        </View>
                    </View>
                </Pressable>
            </Modal>
        </View>
    );
};

export { SelectInput }; 